// SSO de Signa — OAuth 2 Authorization Code + PKCE (S256).
//
// ORIGEN (policies/architecture.md §9 — «absorber, no triplicar»): portado de
// `Luma/luma-ui/src/lib/signa-auth.ts`, el cliente OAuth de referencia del ecosistema, con sus
// lecciones ya pagadas incorporadas: `issuer` es la URL COMPLETA con esquema (el mismo valor exacto
// que `SIGNA_ISSUER` del backend — poner el dominio del FRONTEND ahí rechaza todos los tokens),
// el `state` se genera con CSPRNG y no con `Math.random()`, y todo `fetch` lleva timeout (un fetch
// colgado nunca resuelve ni rechaza y deja el refresco trabado para siempre).
//
// DOS DIFERENCIAS DELIBERADAS CON LUMA, declaradas como manda la política:
//   1. **Nada de tokens en `localStorage`.** Ni el de acceso ni el de refresco. Luma persiste el
//      refresh token para sobrevivir a un F5; Nexa no puede: es la app de licencias, evaluaciones
//      y recibos de sueldo, y un refresh token en storage es una sesión robable por cualquier XSS
//      de cualquier dependencia. El costo es que recargar la pestaña vuelve a pasar por Signa —
//      invisible mientras la sesión del IdP siga viva.
//   2. Los `fetch` van DIRECTO al issuer, sin proxy de mismo origen. Nexa no tiene Pages Functions.
//
// Regla dura de Nexa: **el token de acceso vive en memoria**. Lo único que toca `sessionStorage`
// es el `code_verifier` (y su `state`), y sólo durante el viaje de ida y vuelta al autorizador.

/** Config del cliente público. Sale de variables Vite; ninguna tiene fallback. */
export interface SignaConfig {
  /** URL completa con esquema. Idéntica a `SIGNA_ISSUER` del backend. */
  issuer: string;
  clientId: string;
  /** Origen de `nexa-api`. Vive acá porque es config del mismo bloque de ambiente. */
  apiOrigin: string;
  redirectUri: string;
  scope: string;
}

export interface SignaUser {
  sub: string;
  email: string;
  name?: string;
  avatarUrl?: string;
  orgId: string;
  orgName?: string;
  roles: string[];
  userType: 'human' | 'agent' | 'service';
}

export class SignaAuthError extends Error {
  constructor(message: string, readonly code: string, readonly status?: number) {
    super(message);
    this.name = 'SignaAuthError';
  }
}

/**
 * FAIL-CLOSED (policies/deployment.md §0, lección `EMAIL_PROVIDER = "console"` de Signa): si falta
 * una variable, la app lo dice con el nombre exacto de la variable y no arranca. Un fallback
 * hardcodeado es cómo un ambiente termina apuntando en silencio al Signa equivocado.
 */
function requireEnv(name: string, value: string | undefined): string {
  const trimmed = (value ?? '').trim();
  if (!trimmed) throw new SignaAuthError(`${name} is not configured`, 'configuration_missing');
  return trimmed;
}

export function readSignaConfig(
  env: Record<string, string | undefined> = import.meta.env as unknown as Record<string, string | undefined>,
  origin: string = typeof window !== 'undefined' ? window.location.origin : '',
): SignaConfig {
  return {
    issuer: requireEnv('VITE_SIGNA_ISSUER', env.VITE_SIGNA_ISSUER).replace(/\/+$/, ''),
    clientId: requireEnv('VITE_SIGNA_CLIENT_ID', env.VITE_SIGNA_CLIENT_ID),
    apiOrigin: requireEnv('VITE_API_ORIGIN', env.VITE_API_ORIGIN).replace(/\/+$/, ''),
    redirectUri: `${origin}/auth/callback`,
    scope: 'openid profile email org:read',
  };
}

// ── PKCE y state ────────────────────────────────────────────────────────────────────────────────

function base64Url(bytes: Uint8Array): string {
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function randomToken(byteLength: number): string {
  const bytes = new Uint8Array(byteLength);
  crypto.getRandomValues(bytes);
  return base64Url(bytes);
}

/** S256 es OBLIGATORIO para un cliente público: `plain` deja el verifier a la vista en el redirect. */
export async function createPkcePair(): Promise<{ verifier: string; challenge: string }> {
  const verifier = randomToken(32);
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
  return { verifier, challenge: base64Url(new Uint8Array(digest)) };
}

const VERIFIER_KEY = 'nexa.oauth.code_verifier';
const STATE_KEY = 'nexa.oauth.state';
const RETURN_TO_KEY = 'nexa.oauth.return_to';

// ── HTTP ────────────────────────────────────────────────────────────────────────────────────────

const FETCH_TIMEOUT_MS = 10_000;

async function fetchWithTimeout(url: string, init: RequestInit = {}): Promise<Response> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
  try {
    return await fetch(url, { ...init, signal: controller.signal });
  } finally {
    clearTimeout(timer);
  }
}

interface TokenResponse {
  access_token: string;
  refresh_token?: string;
  expires_in?: number;
  token_type?: string;
}

async function readOAuthError(res: Response): Promise<SignaAuthError> {
  const text = await res.text().catch(() => '');
  let code = 'unknown_error';
  try {
    code = (JSON.parse(text) as { error?: string }).error ?? code;
  } catch {
    // Cuerpo no-JSON: nos quedamos con el código genérico. El texto crudo del IdP no se muestra.
  }
  return new SignaAuthError(mapOAuthError(code), code, res.status);
}

const OAUTH_ERROR_ES: Record<string, string> = {
  invalid_grant: 'El código venció o ya se usó. Volvé a entrar.',
  access_denied: 'Cancelaste el ingreso.',
  login_required: 'Necesitás iniciar sesión.',
  interaction_required: 'Necesitás iniciar sesión.',
  invalid_scope: 'La app está mal configurada en Signa. Avisale a soporte.',
  invalid_client: 'La app está mal configurada en Signa. Avisale a soporte.',
  configuration_missing: 'Falta configuración de identidad en este ambiente.',
};

export function mapOAuthError(code: string | null | undefined): string {
  return (code && OAUTH_ERROR_ES[code]) || 'No pudimos completar el ingreso.';
}

/** Lectura NO verificada del payload, sólo para presentación. Quien autoriza es la API, no la UI. */
function decodeJwtPayload(token: string): Record<string, unknown> {
  try {
    const part = token.split('.')[1];
    if (!part) return {};
    const base64 = part.replace(/-/g, '+').replace(/_/g, '/').padEnd(Math.ceil(part.length / 4) * 4, '=');
    // Vía TextDecoder y no `escape()`: los nombres del directorio llevan acentos y `escape` está
    // deprecado — decodificar mal un apellido es un bug de producto, no de estilo.
    const bytes = Uint8Array.from(atob(base64), (ch) => ch.charCodeAt(0));
    return JSON.parse(new TextDecoder().decode(bytes)) as Record<string, unknown>;
  } catch {
    return {};
  }
}

function userFromToken(token: string): SignaUser {
  const raw = decodeJwtPayload(token);
  const str = (key: string): string | undefined => (typeof raw[key] === 'string' ? (raw[key] as string) : undefined);
  return {
    sub: str('sub') ?? '',
    email: str('email') ?? '',
    name: str('name'),
    avatarUrl: str('avatar_url'),
    orgId: str('org_id') ?? '',
    orgName: str('org_name'),
    roles: Array.isArray(raw.roles) ? (raw.roles as string[]) : [],
    userType: (str('user_type') as SignaUser['userType']) ?? 'human',
  };
}

/** El token de acceso NO trae nombre ni organización legible: los completa `/userinfo`. */
async function fetchUserinfo(config: SignaConfig, accessToken: string): Promise<Record<string, unknown> | null> {
  try {
    const res = await fetchWithTimeout(`${config.issuer}/api/v1/oauth/userinfo`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    if (!res.ok) return null;
    return (await res.json()) as Record<string, unknown>;
  } catch {
    // Best-effort: sin `/userinfo` la sesión es válida igual, sólo con el perfil mínimo del token.
    return null;
  }
}

// ── La sesión ───────────────────────────────────────────────────────────────────────────────────

/** Margen de refresco: el token de Signa dura 900 s y se renueva ~1 min antes de vencer. */
const REFRESH_MARGIN_MS = 60_000;

export type SessionListener = () => void;

export class SignaAuth {
  /** EN MEMORIA. Jamás `localStorage`: ver la nota de cabecera. */
  private accessToken: string | null = null;
  private refreshToken: string | null = null;
  private expiresAt = 0;
  private currentUser: SignaUser | null = null;
  private refreshTimer: ReturnType<typeof setTimeout> | null = null;
  /** Un solo refresco en vuelo: N llamadas paralelas con 401 no rotan el token N veces. */
  private inFlightRefresh: Promise<string> | null = null;
  private listeners = new Set<SessionListener>();

  constructor(readonly config: SignaConfig) {}

  get user(): SignaUser | null {
    return this.currentUser;
  }

  get isAuthenticated(): boolean {
    return Boolean(this.accessToken && this.currentUser);
  }

  subscribe(listener: SessionListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private emit(): void {
    for (const listener of this.listeners) listener();
  }

  /** Paso 1: guarda verifier + state y manda al autorizador. Navegación completa, no `fetch`. */
  async beginLogin(returnTo = '/inbox'): Promise<string> {
    const { verifier, challenge } = await createPkcePair();
    const state = randomToken(16);
    sessionStorage.setItem(VERIFIER_KEY, verifier);
    sessionStorage.setItem(STATE_KEY, state);
    sessionStorage.setItem(RETURN_TO_KEY, returnTo);

    const url = new URL(`${this.config.issuer}/api/v1/oauth/authorize`);
    url.searchParams.set('response_type', 'code');
    url.searchParams.set('client_id', this.config.clientId);
    url.searchParams.set('redirect_uri', this.config.redirectUri);
    url.searchParams.set('scope', this.config.scope);
    url.searchParams.set('state', state);
    url.searchParams.set('code_challenge', challenge);
    url.searchParams.set('code_challenge_method', 'S256');
    return url.toString();
  }

  /** Paso 2: valida el `state`, canjea el código y arma la sesión. Devuelve a dónde volver. */
  async completeLogin(search: string): Promise<string> {
    const params = new URLSearchParams(search);
    const failure = params.get('error');
    if (failure) {
      this.clearHandshake();
      throw new SignaAuthError(mapOAuthError(failure), failure);
    }

    const code = params.get('code');
    const state = params.get('state');
    const expectedState = sessionStorage.getItem(STATE_KEY);
    const verifier = sessionStorage.getItem(VERIFIER_KEY);
    const returnTo = sessionStorage.getItem(RETURN_TO_KEY) ?? '/inbox';

    // El `state` es la defensa CSRF del flujo: sin coincidencia exacta no se canjea nada.
    if (!code || !state || !expectedState || state !== expectedState || !verifier) {
      this.clearHandshake();
      throw new SignaAuthError(
        'El ingreso no se pudo verificar. Volvé a entrar desde Nexa.',
        'invalid_state',
      );
    }

    const body = new URLSearchParams({
      grant_type: 'authorization_code',
      client_id: this.config.clientId,
      redirect_uri: this.config.redirectUri,
      code,
      code_verifier: verifier,
    });

    const res = await fetchWithTimeout(`${this.config.issuer}/api/v1/oauth/token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: body.toString(),
    });
    this.clearHandshake();
    if (!res.ok) throw await readOAuthError(res);

    await this.adoptTokens((await res.json()) as TokenResponse);
    return returnTo;
  }

  /** Token vigente para la API. Refresca solo si está por vencer; si no hay sesión, avisa. */
  async getAccessToken(): Promise<string> {
    if (!this.accessToken) throw new SignaAuthError('Necesitás iniciar sesión.', 'login_required');
    if (Date.now() < this.expiresAt - REFRESH_MARGIN_MS) return this.accessToken;
    return this.refresh();
  }

  /**
   * ROTACIÓN: Signa devuelve un refresh token NUEVO en cada canje y el anterior queda quemado.
   * Guardar el viejo es garantizar que el próximo refresco falle.
   */
  refresh(): Promise<string> {
    if (this.inFlightRefresh) return this.inFlightRefresh;
    const token = this.refreshToken;
    if (!token) {
      return Promise.reject(new SignaAuthError('Tu sesión venció. Volvé a entrar.', 'login_required'));
    }

    this.inFlightRefresh = (async () => {
      const body = new URLSearchParams({
        grant_type: 'refresh_token',
        client_id: this.config.clientId,
        refresh_token: token,
      });
      const res = await fetchWithTimeout(`${this.config.issuer}/api/v1/oauth/token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      if (!res.ok) {
        // Refresh muerto = sesión muerta: se limpia todo en vez de dejar media sesión zombi.
        this.logout();
        throw await readOAuthError(res);
      }
      await this.adoptTokens((await res.json()) as TokenResponse);
      return this.accessToken as string;
    })().finally(() => {
      this.inFlightRefresh = null;
    });

    return this.inFlightRefresh;
  }

  logout(): void {
    this.accessToken = null;
    this.refreshToken = null;
    this.expiresAt = 0;
    this.currentUser = null;
    if (this.refreshTimer) clearTimeout(this.refreshTimer);
    this.refreshTimer = null;
    this.clearHandshake();
    this.emit();
  }

  private clearHandshake(): void {
    sessionStorage.removeItem(VERIFIER_KEY);
    sessionStorage.removeItem(STATE_KEY);
    sessionStorage.removeItem(RETURN_TO_KEY);
  }

  private async adoptTokens(tokens: TokenResponse): Promise<void> {
    if (!tokens.access_token) throw new SignaAuthError('Signa no devolvió un token.', 'invalid_grant');
    this.accessToken = tokens.access_token;
    // Rotación: siempre el nuevo. Si no vino uno, el anterior sigue siendo el vigente.
    if (tokens.refresh_token) this.refreshToken = tokens.refresh_token;
    this.expiresAt = Date.now() + (tokens.expires_in ?? 900) * 1000;
    this.currentUser = userFromToken(tokens.access_token);
    this.scheduleRefresh();
    this.emit();

    const info = await fetchUserinfo(this.config, tokens.access_token);
    if (!info || this.accessToken !== tokens.access_token || !this.currentUser) return;
    const memberships = Array.isArray(info.memberships)
      ? (info.memberships as Array<Record<string, unknown>>)
      : [];
    const membership = memberships.find((m) => m.organization_id === this.currentUser?.orgId);
    this.currentUser = {
      ...this.currentUser,
      name: (info.name as string | undefined) ?? this.currentUser.name,
      avatarUrl: (info.avatar_url as string | undefined) ?? this.currentUser.avatarUrl,
      orgName: (membership?.name as string | undefined) ?? this.currentUser.orgName,
    };
    this.emit();
  }

  /** Refresco proactivo ~1 min antes de vencer: la persona no ve un parpadeo de sesión. */
  private scheduleRefresh(): void {
    if (this.refreshTimer) clearTimeout(this.refreshTimer);
    const delay = Math.max(this.expiresAt - Date.now() - REFRESH_MARGIN_MS, 1_000);
    this.refreshTimer = setTimeout(() => {
      void this.refresh().catch(() => {
        // El fallo ya dejó la sesión limpia en `refresh()`; el guard de ruta lleva a /login.
      });
    }, delay);
  }
}
